import { memo, useCallback, useEffect, useState } from 'react';
import { getApolloClient } from '@frameio/graphql-client';
import { NotificationsList } from '@frameio/notifications/components/NotificationsList';
import { NotificationsListFilter } from '@frameio/notifications/components/NotificationsListFilter';
import { NOTIFICATIONS_LIST_FILTERS } from '@frameio/notifications/components/NotificationsListFilter/types';
import { PAGE_SIZE } from '@frameio/notifications/constants';
import { useShouldShowTruncatedAccessRequestsList } from '@frameio/notifications/contexts/ShouldShowTruncatedAccessRequestsList';
import {
  GetNotificationGroups,
  useGetNotificationGroupsQuery,
  useHasUnreadNotificationsFragment,
  useMarkAllNotificationsAsReadMutation,
  useUnopenedNotificationGroupsCountFragment,
} from '@frameio/notifications/gql';
import { useReportNotificationGroupsLastOpened } from '@frameio/notifications/hooks';
import {
  markAllCachedNotificationGroupsAsRead,
  modifyCachedNotificationGroupsCount,
} from '@frameio/notifications/utils';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing';
import { LinkWithPrefetch } from '@frameio/shared/routing/components/LinkWithPrefetch';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { reportError } from '@frameio/telemetry';
import {
  Box,
  IconButton2,
  InlineButton,
  Separator,
  SheetHead,
  Text,
} from '@frameio/vapor';
import { IconCog } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/NotificationSheet';

TEXT.DEFINE({
  /** Label for filtering the notifications list to only unread items */
  filterUnread: 'Filter Unread',
  /** Button label that marks all notifications as read */
  markAllAsRead: 'Mark all as read',
  /** Icon button label that navigates to notification settings from the notifications sheet */
  notificationsSettings: 'Notifications Settings',
  /** Sheet title shown at the top of the notifications panel */
  title: 'Notifications',
});

export const NotificationSheet = memo(() => {
  const accountId = useActiveAccountId();
  const client = getApolloClient();
  const track = useTrackUnsafe();

  const [filter, setFilter] = useState(NOTIFICATIONS_LIST_FILTERS.ALL);
  const [isLoading, setIsLoading] = useState(false);

  const { data: totalCountNotificationsData } = useGetNotificationGroupsQuery({
    variables: {
      accountId,
      page: {
        first: PAGE_SIZE,
      },
    },
  });
  const totalCountNotifications =
    totalCountNotificationsData?.account?.notificationGroups?.nodes.length;
  const hasNotifications =
    typeof totalCountNotifications === 'number' && totalCountNotifications > 0;

  const { setShouldShowTruncatedAccessRequestsList } =
    useShouldShowTruncatedAccessRequestsList();

  useEffect(() => {
    /** on first load, notifications may not have finished fetching,
     * so set this to the default state when the sheet is first opened
     */
    setShouldShowTruncatedAccessRequestsList(hasNotifications);
    return () => {
      /** reset to default state when the sheet is unmounted */
      setShouldShowTruncatedAccessRequestsList(hasNotifications);
    };
  }, [hasNotifications, setShouldShowTruncatedAccessRequestsList]);

  const { data: hasUnreadsData } = useHasUnreadNotificationsFragment({
    from: {
      __typename: 'Account',
      id: accountId,
    },
  });

  const { data: unopenedCountData } =
    useUnopenedNotificationGroupsCountFragment({
      from: {
        __typename: 'Account',
        id: accountId,
      },
    });

  const hasUnreads = hasUnreadsData?.notificationGroups?.hasUnreads;
  const newNotificationsCount =
    unopenedCountData?.notificationGroups?.totalCount;

  const { reportNotificationGroupsLastOpenedMutation } =
    useReportNotificationGroupsLastOpened({});

  const [markAllNotificationsAsReadMutation] =
    useMarkAllNotificationsAsReadMutation();

  const updateNotificationsList = useCallback(async () => {
    if (newNotificationsCount) {
      /** Optimistically reset the new notifications count so the refresh
       * toast is not shown */
      modifyCachedNotificationGroupsCount({
        accountId,
        client,
        timestamp: null,
        totalCount: 0,
      });
    }

    const unreadOnly =
      filter === NOTIFICATIONS_LIST_FILTERS.UNREAD ? { unreadOnly: true } : {};

    const refetchData = await client.query({
      query: GetNotificationGroups,
      variables: {
        accountId,
        page: {
          first: PAGE_SIZE,
        },
        ...unreadOnly,
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    });

    const lastOpenedNotificationId =
      refetchData?.data?.account?.notificationGroups?.mostRecentNotification
        ?.id;

    if (filter === NOTIFICATIONS_LIST_FILTERS.UNREAD) {
      track('notifications-list-filtered', {
        filter_selected: filter,
      });
    }

    if (!accountId || !lastOpenedNotificationId) return;

    if (newNotificationsCount) {
      await reportNotificationGroupsLastOpenedMutation({
        variables: {
          input: {
            accountId,
            lastOpenedNotificationId,
          },
        },
      });
    }
  }, [
    accountId,
    client,
    reportNotificationGroupsLastOpenedMutation,
    filter,
    track,
    newNotificationsCount,
  ]);

  const markAllNotificationsAsRead = useCallback(() => {
    setIsLoading(true);
    markAllNotificationsAsReadMutation({
      variables: {
        input: {
          accountId,
        },
      },
      onError: (err) => {
        reportError(err, {
          message: 'Failed to mark all notifications as read',
        });
        setIsLoading(false);
      },
      update: () => {
        markAllCachedNotificationGroupsAsRead({ accountId, filter });
      },
      onCompleted: async () => {
        if (
          newNotificationsCount === 0 &&
          filter === NOTIFICATIONS_LIST_FILTERS.ALL
        ) {
          await client.query({
            query: GetNotificationGroups,
            variables: {
              accountId,
              page: {
                first: PAGE_SIZE,
              },
            },
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
          });
        }
        setIsLoading(false);
        track('notifications-marked-all-as-read');
      },
    });
  }, [
    accountId,
    client,
    filter,
    markAllNotificationsAsReadMutation,
    newNotificationsCount,
    track,
  ]);

  useEffect(() => {
    updateNotificationsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const isDisabled = !hasUnreads || isLoading;

  return (
    <>
      <SheetHead
        title={TEXT.title}
        toolbar={
          <LinkWithPrefetch
            href={getLink({
              page: 'settings',
              section: 'notifications',
            })}
          >
            <IconButton2
              data-testid="notifications-sheet-settings-button"
              icon={<IconCog />}
              label={TEXT.notificationsSettings}
              onPress={() =>
                track('notification-settings-navigated', {
                  from: 'notifications sheet',
                })
              }
              size="small"
              variant="tertiary"
            />
          </LinkWithPrefetch>
        }
      />
      <Box
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        paddingY={12}
      >
        <Box paddingLeft={8}>
          <NotificationsListFilter filter={filter} onPress={setFilter} />
        </Box>
        <Box paddingRight={16}>
          <InlineButton
            isDisabled={isDisabled}
            onPress={markAllNotificationsAsRead}
            variant="primary"
          >
            <Text
              color="inherit"
              style={{ cursor: isDisabled ? 'default' : 'pointer' }}
              variant="body"
            >
              {TEXT.markAllAsRead}
            </Text>
          </InlineButton>
        </Box>
      </Box>
      <Separator />
      <NotificationsList filter={filter} setFilter={setFilter} />
    </>
  );
});
